import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {MapPin, Phone, Mail, Clock, Building2} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const Contact = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const partnerLocations = [
    {
      region: 'Surrey',
      clinics: [
        {
          name: 'Surrey (Guildford)',
          address: '15300 105 Ave, Unit #17',
          city: 'Surrey, BC V3R 6A7'
        },
        {
          name: 'Surrey (Nordel)',
          address: '12565 88 Ave, Unit #101',
          city: 'Surrey, BC V3W 3J7'
        }
      ]
    },
    {
      region: 'Fraser Valley',
      clinics: [
        {
          name: 'Abbotsford',
          address: '2180 Gladwin Rd, Unit #102A',
          city: 'Abbotsford, BC V2S 0H4'
        },
        {
          name: 'Chilliwack',
          address: '9123 Mary St, Unit #101',
          city: 'Chilliwack, BC V2P 4H7'
        }
      ]
    },
    {
      region: 'Vancouver',
      clinics: [
        {
          name: 'Broadway',
          address: '805 W Broadway, Unit #109',
          city: 'Vancouver, BC V5Z 1H2'
        },
        {
          name: 'Main Street',
          address: '6410 Main St',
          city: 'Vancouver, BC V5W 2V4'
        },
        {
          name: 'Joyce',
          address: '5150 Joyce St',
          city: 'Vancouver, BC V5R 4H1'
        }
      ]
    }
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'saba@haventravelhealth.ca',
      link: 'mailto:saba@haventravelhealth.ca'
    },
    {
      icon: Clock,
      label: 'Hours',
      value: '8AM-4PM Monday-Friday',
      link: null
    },
    {
      icon: Building2,
      label: 'Service Area',
      value: 'Vancouver, Lower Mainland, and Fraser Valley',
      link: null
    }
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Touch</span>
            </h1>
            
            <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-200 leading-relaxed">
              Virtual travel health consultations across British Columbia
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 max-w-4xl mx-auto">
            {contactInfo.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white dark:from-navy-800 dark:to-navy-800/50 p-6 rounded-2xl border border-gray-200 dark:border-white/10 hover:border-cyan-500/50 transition-all shadow-lg hover:shadow-xl">
                
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  {item.label}
                </h3>
                
                {item.link ? (
                  <a 
                    href={item.link}
                    className="text-navy-900 dark:text-white font-medium hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-navy-900 dark:text-white font-medium">
                    {item.value}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Clinic Locations */}
      <section className="py-16 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Haven Travel Health – Partner Pharmacy Locations
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              After your virtual consultation, vaccines are administered at our partner pharmacies across BC.
            </p>
          </div>

          <div className="space-y-8">
            {partnerLocations.map((location, regionIndex) => (
              <motion.div
                key={location.region}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: regionIndex * 0.1 }}
                className="bg-white dark:bg-navy-900 rounded-2xl p-8 border border-gray-200 dark:border-white/10 shadow-lg">
                
                <h3 className="text-2xl font-bold text-navy-900 dark:text-white mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  {location.region}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {location.clinics.map((clinic, clinicIndex) => (
                    <motion.div
                      key={clinic.name}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: clinicIndex * 0.1 }}
                      className="bg-gradient-to-br from-gray-50 to-white dark:from-navy-800 dark:to-navy-800/50 p-6 rounded-xl border border-gray-200 dark:border-white/10 hover:border-cyan-500/50 transition-all group">
                      
                      <h4 className="font-bold text-navy-900 dark:text-white mb-3 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                        {clinic.name}
                      </h4>
                      
                      <div className="space-y-2 text-gray-600 dark:text-gray-400">
                        <p className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-1 text-cyan-500 flex-shrink-0" />
                          <span className="text-sm">{clinic.address}</span>
                        </p>
                        <p className="text-sm ml-6">{clinic.city}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="px-10 py-4 bg-gradient-to-r from-coral-500 to-coral-400 hover:from-coral-400 hover:to-coral-500 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
              Book Virtual Consult
            </button>
            <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm">
              Pharmacy location will be coordinated based on your preference after consultation
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            Ready to Prepare for Your Trip?
          </h2>
          <p className="text-gray-200 text-lg mb-8 leading-relaxed">
            Book a virtual consultation with Dr. Saba Baig, PharmD in partnership with VoyageVax. Get personalized travel health advice and vaccine recommendations.
          </p>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="px-10 py-4 bg-gradient-to-r from-coral-500 to-coral-400 hover:from-coral-400 hover:to-coral-500 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
            Book Your Consultation
          </button>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default Contact;
